import { useMemo } from 'react';
import { Link, useNavigate, Routes, Route } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  ListTodo,
  Plus,
  Settings2,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Progress } from '@/app/components/ui/progress';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { WorkTemplatesPage } from './templates/work-templates-page';
import { WorkTemplateDetailPage } from './templates/work-template-detail-page';
import { WorkTasksPage } from './work-tasks-page';
import { WorkTaskDetailPage } from './work-task-detail-page';
import { WorkHistoryPage } from './history/work-history-page';
import { useWorkTasks, useWorkTemplates } from '@/hooks/use-work';
import { useProjects } from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import type { WorkTask } from '@/lib/types';

function taskStatusLabel(status: WorkTask['status']) {
  switch (status) {
    case 'todo':
      return 'To Do';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'blocked':
      return 'Blocked';
  }
}

function TaskProgressRow({ task, projectLabel }: { task: WorkTask; projectLabel?: string }) {
  const percent = Math.round((task.progress ?? 0) * 100);
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-medium text-gray-900 dark:text-foreground text-sm truncate">{task.title}</h4>
          {task.failed_steps > 0 && (
            <Badge variant="destructive" className="text-xs">
              {task.failed_steps}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-muted-foreground mt-1">
          <span>{projectLabel || `Project ${task.project}`}</span>
          <span>·</span>
          <span>{taskStatusLabel(task.status)}</span>
          {task.assigned_to_name && <span>· {task.assigned_to_name}</span>}
        </div>
      </div>
      <div className="w-24 sm:w-32 shrink-0">
        <Progress value={percent} className="h-2" />
        <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1 text-right">{percent}%</p>
      </div>
      <Button variant="ghost" size="icon" asChild className="shrink-0">
        <Link to={`/work/tasks/${task.id}`}>
          <ChevronRight className="h-4 w-4" />
        </Link>
      </Button>
    </div>
  );
}

function WorkDashboard() {
  const navigate = useNavigate();
  const { hasMinRole } = usePermissions();
  const canManage = hasMinRole('Manager');
  const { data: tasksData, isLoading: tasksLoading } = useWorkTasks({
    page_size: 10,
    ordering: '-updated_at',
  });
  const { data: projectsData } = useProjects({ page_size: 1000 }, true);
  const { data: templatesData, isLoading: templatesLoading } = useWorkTemplates({ page_size: 5 }, canManage);

  const recentTasks = tasksData?.results || [];
  const inProgress = recentTasks.filter((t) => t.status === 'in_progress');
  const projectMap = useMemo(() => {
    const map: Record<number, string> = {};
    projectsData?.results.forEach((p) => {
      map[p.id] = `${p.project_number} — ${p.name}`;
    });
    return map;
  }, [projectsData]);

  const projectLabel = (task: WorkTask) => projectMap[task.project] || `Project ${task.project}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1><img src="/logos/h3project.svg" alt="H3Project" className="h-7" /></h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">Work management — tasks and checklists under foundation Projects.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/work/tasks">
              <ListTodo className="h-4 w-4 mr-2" />
              All Tasks
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/work/templates">
              <ClipboardList className="h-4 w-4 mr-2" />
              Templates
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-blue-600" />
              In Progress
            </CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/work/tasks?status=in_progress">View all</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {tasksLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : inProgress.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <p className="text-sm text-gray-500 dark:text-muted-foreground">No in-progress checklists.</p>
                <Button variant="outline" size="sm" className="mt-3" asChild>
                  <Link to="/work/tasks?status=todo">Start a task</Link>
                </Button>
              </div>
            ) : (
              <div>
                {inProgress.slice(0, 5).map((task) => (
                  <TaskProgressRow key={task.id} task={task} projectLabel={projectLabel(task)} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {canManage && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-600" />
                New from template
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {templatesLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : templatesData?.results.length ? (
                <>
                  <Select onValueChange={(value) => navigate(`/work/tasks?template=${value}`)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templatesData.results.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to="/work/templates">
                      <Settings2 className="h-4 w-4 mr-2" />
                      Manage templates
                    </Link>
                  </Button>
                </>
              ) : (
                <div className="text-center py-6 border border-dashed rounded-lg">
                  <p className="text-sm text-gray-500 dark:text-muted-foreground">No templates yet.</p>
                  <Button variant="outline" size="sm" className="mt-3" asChild>
                    <Link to="/work/templates">Create template</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ListTodo className="h-5 w-5 text-gray-600 dark:text-muted-foreground" />
            Recent Tasks
          </CardTitle>
          <Button variant="outline" size="sm" asChild>
            <Link to="/work/tasks">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {tasksLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : recentTasks.length === 0 ? (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <p className="text-sm text-gray-500 dark:text-muted-foreground">No tasks yet.</p>
              <Button className="mt-3" asChild>
                <Link to="/work/tasks">Create task</Link>
              </Button>
            </div>
          ) : (
            <div>
              {recentTasks.slice(0, 8).map((task) => (
                <TaskProgressRow key={task.id} task={task} projectLabel={projectLabel(task)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function WorkPage() {
  return (
    <Routes>
      <Route index element={<WorkDashboard />} />
      <Route path="tasks" element={<WorkTasksPage />} />
      <Route path="tasks/:id" element={<WorkTaskDetailPage />} />
      <Route path="run/:id" element={<WorkTaskDetailPage />} />
      <Route path="history" element={<WorkHistoryPage />} />
      <Route path="templates" element={<WorkTemplatesPage />} />
      <Route path="templates/:id" element={<WorkTemplateDetailPage />} />
    </Routes>
  );
}
